import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlaskConical, Play, Plus, X, Copy, CheckCircle2, ExternalLink, Terminal } from "lucide-react";
import { BACKEND } from "@/lib/api";

const TEST_TOKENS = { stripe: "pm_card_visa", square: "cnon:card-nonce-ok" };

export default function CheckoutTester({ sub, onDone }) {
  const provider = sub.active_provider;
  const [amount, setAmount] = useState("19.99");
  const [email, setEmail] = useState("jane@shop.com");
  const [token, setToken] = useState(TEST_TOKENS[provider] || "");
  const [items, setItems] = useState([{ name: "T-Shirt", quantity: 1, price: "9.99" }]);
  const [ship, setShip] = useState(false);
  const [addr, setAddr] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const setA = (k, v) => setAddr((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showReq, setShowReq] = useState(false);

  const updateItem = (i, key, val) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((p) => [...p, { name: "", quantity: 1, price: "" }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const cleanItems = items
    .filter((i) => i.name.trim())
    .map((i) => ({ name: i.name.trim(), quantity: Number(i.quantity) || 1, ...(i.price !== "" ? { price: parseFloat(i.price) } : {}) }));
  const endpointUrl = `${BACKEND}/api/checkout/${sub.checkout_key}`;
  const shippingBody = ship && addr.line1.trim() ? { shipping: { ...addr } } : {};
  const reqBody = { amount: parseFloat(amount) || 0, currency: "USD", token, customer: { name: "Jane Doe", email }, ...(cleanItems.length ? { items: cleanItems } : {}), ...shippingBody };
  const curl = `curl -X POST "${endpointUrl}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(reqBody)}'`;
  const willItemize = provider === "square" && cleanItems.length > 0 && cleanItems.every((i) => i.price > 0);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(endpointUrl, reqBody);
      setResult({ ok: true, data });
      toast.success("Payment succeeded");
      onDone?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResult({ ok: false, data: detail || err.message });
      toast.error("Payment failed");
      onDone?.();
    } finally {
      setBusy(false);
    }
  };

  if (!provider) {
    return (
      <Card className="p-6 border-border/70 text-sm text-muted-foreground">
        Configure and activate a provider (Square or Stripe) to run a test charge.
      </Card>
    );
  }

  return (
    <Card className="p-5 border-border/70 space-y-4">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Test Charge</h3>
        <span className="text-[10px] font-mono text-muted-foreground">via {provider}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Amount (USD)</Label>
          <Input data-testid="tester-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Customer Email</Label>
          <Input data-testid="tester-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="font-mono" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Card Token / Source ID</Label>
        <Input data-testid="tester-token-input" value={token} onChange={(e) => setToken(e.target.value)} className="font-mono text-sm" />
        <p className="text-[11px] text-muted-foreground font-mono">
          Test token pre-filled ({provider === "stripe" ? "pm_card_visa" : "cnon:card-nonce-ok"}).
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Products (optional)</Label>
          <Button type="button" variant="ghost" size="sm" data-testid="tester-add-item-btn" onClick={addItem} className="h-7 gap-1 text-xs">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
        {items.map((it, i) => (
          <div key={i} className="flex gap-2" data-testid={`tester-item-row-${i}`}>
            <Input placeholder="Product name" data-testid={`tester-item-name-${i}`} value={it.name} onChange={(e) => updateItem(i, "name", e.target.value)} className="flex-1 font-mono text-sm" />
            <Input placeholder="Qty" type="number" min="1" data-testid={`tester-item-qty-${i}`} value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="w-16 font-mono text-sm" />
            <Input placeholder="Price" data-testid={`tester-item-price-${i}`} value={it.price} onChange={(e) => updateItem(i, "price", e.target.value)} className="w-24 font-mono text-sm" />
            <Button type="button" variant="ghost" size="icon" data-testid={`tester-item-remove-${i}`} onClick={() => removeItem(i)} className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground cursor-pointer">
          <input type="checkbox" data-testid="tester-ship-toggle" checked={ship} onChange={(e) => setShip(e.target.checked)} className="accent-primary" />
          Shipping address (optional)
        </label>
        {ship && (
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="ship-name" placeholder="Recipient name" value={addr.name} onChange={(e) => setA("name", e.target.value)} className="col-span-2 font-mono text-sm" />
            <Input data-testid="ship-line1" placeholder="Address line 1" value={addr.line1} onChange={(e) => setA("line1", e.target.value)} className="col-span-2 font-mono text-sm" />
            <Input data-testid="ship-line2" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setA("line2", e.target.value)} className="col-span-2 font-mono text-sm" />
            <Input data-testid="ship-city" placeholder="City" value={addr.city} onChange={(e) => setA("city", e.target.value)} className="font-mono text-sm" />
            <Input data-testid="ship-state" placeholder="State" value={addr.state} onChange={(e) => setA("state", e.target.value)} className="font-mono text-sm" />
            <Input data-testid="ship-postal" placeholder="Postal code" value={addr.postal_code} onChange={(e) => setA("postal_code", e.target.value)} className="font-mono text-sm" />
            <Input data-testid="ship-country" placeholder="Country (US)" value={addr.country} onChange={(e) => setA("country", e.target.value)} className="font-mono text-sm" />
            <Input data-testid="ship-phone" placeholder="Phone" value={addr.phone} onChange={(e) => setA("phone", e.target.value)} className="col-span-2 font-mono text-sm" />
          </div>
        )}
      </div>

      {willItemize && (
        <div data-testid="tester-itemize-hint" className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] font-mono text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Square Orders API: an itemized order will be created &amp; the amount will use the line-item total.
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button data-testid="tester-run-btn" onClick={run} disabled={busy} className="gap-2">
          <Play className="h-4 w-4" />
          {busy ? "Charging…" : "Run Test Charge"}
        </Button>
        <Button type="button" variant="outline" data-testid="tester-toggle-request" onClick={() => setShowReq((v) => !v)} className="gap-2">
          <Terminal className="h-4 w-4" />
          {showReq ? "Hide request" : "Show request"}
        </Button>
      </div>

      {showReq && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Request (copy &amp; share)</Label>
            <Button type="button" variant="ghost" size="sm" data-testid="tester-copy-curl" onClick={() => { navigator.clipboard.writeText(curl); toast.success("cURL copied"); }} className="h-7 gap-1 text-xs">
              <Copy className="h-3.5 w-3.5" /> Copy cURL
            </Button>
          </div>
          <pre data-testid="tester-request-curl" className="rounded-md border border-border/70 bg-muted/30 p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{curl}</pre>
        </div>
      )}

      {result?.ok && (
        <div data-testid="tester-summary" className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2">
          <div className="flex items-center gap-2 text-emerald-400 font-heading font-semibold text-sm">
            <CheckCircle2 className="h-4 w-4" /> Payment {result.data.status}
          </div>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-xs">
            <span className="text-muted-foreground">Amount charged</span>
            <span>${Number(result.data.amount).toFixed(2)} {result.data.currency}</span>
            <span className="text-muted-foreground">Payment ID</span>
            <span className="truncate">{result.data.payment_id}</span>
            {result.data.order_id ? (
              <>
                <span className="text-muted-foreground">Square Order ID</span>
                <span className="truncate text-emerald-400" data-testid="tester-order-id">{result.data.order_id}</span>
              </>
            ) : (
              <>
                <span className="text-muted-foreground">Order</span>
                <span>plain payment (no itemized order)</span>
              </>
            )}
          </div>
          {result.data.order_id && (
            <div className="flex items-center gap-1 text-[11px] font-mono text-emerald-400">
              <CheckCircle2 className="h-3 w-3" /> Orders API working — itemized order created &amp; linked to this payment
            </div>
          )}
          {result.data.receipt_url && (
            <a href={result.data.receipt_url} target="_blank" rel="noreferrer" data-testid="tester-receipt-link" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <ExternalLink className="h-3 w-3" /> View Square receipt
            </a>
          )}
        </div>
      )}

      {result && (
        <pre
          data-testid="tester-result"
          className={`rounded-md border p-3 text-xs font-mono overflow-x-auto ${result.ok ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/5" : "border-destructive/40 text-destructive bg-destructive/5"}`}
        >
          {JSON.stringify(result.data, null, 2)}
        </pre>
      )}
    </Card>
  );
}
